import React from 'react';
import type { ComponentProps, MouseEvent } from 'react';
import styled from '@emotion/styled';
import type {
  BackgroundColorProps,
  BorderRadiusProps,
  BoxShadowProps,
  ColorProps,
  DisplayProps,
  HeightProps,
  PositionProps,
  WidthProps,
} from 'styled-system';
import {
  backgroundColor,
  borderRadius,
  boxShadow,
  color,
  display,
  height,
  position,
  width,
} from 'styled-system';

import DiscountLabels from 'common/components/DiscountLabels';
import { Flex } from 'common/components/Flex';
import WishListButton from 'common/components/WishlistButton';
import { focusOutlineCSS } from 'common/theme/styles';
import {
  boxSizing,
  flexShrink,
  textDecoration,
  themeHeight,
  themeRight,
  themeTop,
} from 'common/theme/system-utilities';
import type {
  BoxSizingProps,
  FlexShrinkProps,
  TextDecorationProps,
  ThemeHeightProps,
  ThemeRightProps,
  ThemeTopProps,
} from 'common/theme/system-utilities';
import theme from 'common/theme/theme';
import { formatPrice } from 'common/utils/format-price';
import { LABELS_BOTTOM, LABELS_LEFT } from './constants';
import { useSlideClickHandler } from './hooks/handlers';
import RecommendationSlideDetails from './RecommendationSlideDetails';
import RecommendationSlideImage from './RecommendationSlideImage';
import type { RecommendationsSlideProps } from './types';
import { getListEventValue } from './utils';

interface RecommendationSlideContentProps extends RecommendationsSlideProps {
  onMouseOver?: (event: MouseEvent) => void;
}

const UnstyledSlide = styled.a<
  DisplayProps &
    PositionProps &
    ColorProps &
    BackgroundColorProps &
    BorderRadiusProps &
    BoxShadowProps &
    TextDecorationProps &
    ThemeHeightProps &
    WidthProps &
    BoxSizingProps &
    FlexShrinkProps
>`
  ${display};
  ${position};
  ${color};
  ${backgroundColor};
  ${borderRadius};
  ${boxShadow};
  ${textDecoration};
  ${themeHeight};
  ${width};
  ${focusOutlineCSS};
  ${boxSizing};
  ${flexShrink};

  &:hover,
  &:active {
    box-shadow: ${theme.shadows.low};
  }
`;

const Slide = (props: ComponentProps<typeof UnstyledSlide>) => (
  <UnstyledSlide
    display="block"
    position="relative"
    color="sys.neutral.text.strong"
    bg="sys.neutral.background.default"
    borderRadius="medium"
    boxShadow="extraLow"
    textDecoration="none"
    themeHeight="100%"
    width="100%"
    boxSizing="border-box"
    flexShrink="0"
    {...props}
  />
);

const ImageContainer = styled.div<PositionProps>`
  ${position};
`;

const InnerContainer = (props: ComponentProps<typeof Flex>) => (
  <Flex flexDirection="column" p="sp_8" {...props} />
);

const UnstyledWishlistButton = styled(WishListButton)<
  PositionProps & ThemeRightProps & ThemeTopProps & HeightProps
>`
  ${position};
  ${themeRight};
  ${themeTop};
  ${height};
`;

const StyledWishlistButton = (props: ComponentProps<typeof UnstyledWishlistButton>) => (
  <UnstyledWishlistButton
    position="absolute"
    themeRight="sp_12"
    themeTop="sp_12"
    height="32px"
    {...props}
  />
);

const getConstructorMetaData = ({
  parentSku,
  name,
  sku,
  strategyId,
  prices,
  dyDecisionId,
}: Pick<
  RecommendationSlideContentProps,
  'parentSku' | 'name' | 'sku' | 'strategyId' | 'prices' | 'dyDecisionId'
>) =>
  !dyDecisionId
    ? {
        'data-cnstrc-item': 'recommendation',
        'data-cnstrc-item-id': parentSku,
        'data-cnstrc-item-name': name,
        'data-cnstrc-item-variation-id': sku,
        'data-cnstrc-strategy-id': strategyId,
        'data-cnstrc-item-price': formatPrice(prices.special?.value ?? prices.regular?.value ?? ''),
      }
    : {};

const RecommendationSlideContent = ({
  onMouseOver,
  index,
  energyClass,
  url,
  tracking,
  type,
  labels_v2,
  prices,
  delivery,
  name,
  sku,
  parentSku,
  withWishlistButton,
  dyDecisionId,
  dySlotId,
  strategyId,
  eventDataList,
  campaigns,
  withDiscountLabelBackButton,
  isSmallVariant = false,
  ...slideProps
}: RecommendationSlideContentProps) => {
  const list = getListEventValue({ eventDataList, type });
  const { onSlideClick, wishListButtonId } = useSlideClickHandler({
    articleSku: sku,
    articleTracking: tracking,
    slideIndex: index,
    productEventDataListType: list,
    dyDecisionId,
    dySlotId,
  });

  return (
    <Slide
      data-testid="recommendation-slide"
      href={url}
      onClick={onSlideClick}
      onMouseOver={onMouseOver}
      {...getConstructorMetaData({ parentSku, name, sku, strategyId, prices, dyDecisionId })}
    >
      <InnerContainer>
        <ImageContainer position="relative">
          <RecommendationSlideImage
            {...slideProps}
            dataTestId={`recommendation-slide-image-${index}`}
            alt={name}
          />
          {labels_v2 && (
            <DiscountLabels
              labels_v2={labels_v2}
              themeBottom={LABELS_BOTTOM}
              themeLeft={LABELS_LEFT}
              brand={slideProps?.brand?.name ?? ''}
              campaigns={campaigns}
              hideHome24Tipp
              withBackButton={withDiscountLabelBackButton}
              isSmallVariant={isSmallVariant}
            />
          )}
        </ImageContainer>
        <RecommendationSlideDetails
          name={name}
          prices={prices}
          delivery={delivery}
          energyClass={energyClass}
          labels_v2={labels_v2}
        />
      </InnerContainer>
      {withWishlistButton && (
        <StyledWishlistButton
          sku={sku}
          productName={name}
          tracking={tracking}
          id={wishListButtonId}
        />
      )}
    </Slide>
  );
};

export default RecommendationSlideContent;
